"use client";

import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { ArrowRight, Lock, MessageSquarePlus, ShieldOff } from "lucide-react";
import { ChatInput, ChatMessages, ChatHistory, ChatSearchDialog } from ".";
import { useMediaQuery, useToast, useAuth } from "@/app/hooks";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { TaiZenLogo, Button, Loading } from "@/app/components/ui";
import { cn } from "@/app/lib/utils";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { PROJECTS, DOMAIN, METHOD_TYPE } from "@/app/lib/enums";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { api_url } from "@/app/api";
import { logger } from "@/app/lib/logger";

type ChatCompleteProps = {
   isShowChatHistory?: boolean;
   isShowChatInput?: boolean;
   shareKey?: string;
};

const MIN_CHAT_HISTORY_SIDEBAR_WIDTH = 240;
const CHAT_HISTORY_SIDEBAR_VIEWPORT_GUTTER = 80;

function getViewportClampedSidebarWidth(width: number, viewportWidth: number) {
   if (!viewportWidth) return width;

   const viewportMaxWidth = Math.max(
      MIN_CHAT_HISTORY_SIDEBAR_WIDTH,
      viewportWidth - CHAT_HISTORY_SIDEBAR_VIEWPORT_GUTTER
   );

   return Math.min(width, viewportMaxWidth);
}

export function ChatComplete(props: ChatCompleteProps) {
   const {
      isShowChatHistory = true,
      isShowChatInput = true,
      shareKey: shareKeyProp = ""
   } = props;
   const [activeShareKey, setActiveShareKey] = useState(shareKeyProp);
   const [isForking, setIsForking] = useState(false);
   const [isChatUnavailable, setIsChatUnavailable] = useState(false);
   const [isChatHistorySidebarResizing, setIsChatHistorySidebarResizing] =
      useState(false);
   const [viewportWidth, setViewportWidth] = useState(0);

   const handleShareComplete = useCallback(() => {
      setActiveShareKey("");
   }, []);
   const {
      loadingChat: loading,
      errorChat: error,
      initModel,
      setHistoryItem,
      isOpenHistoryChat,
      setOpenHistoryChat,
      setSearchDialogOpen,
      resetState,
      historyItem,
      lastSendMessageEvent,
      isTemporaryChat,
      triggerHistoryRefetch,
      sharedKnowledgeBaseId,
      chatHistorySidebarWidth,
      setChatHistorySidebarWidth
   } = useStateHistoryChatPage();
   const locale = useLocale();
   const router = useRouter();
   const { toast } = useToast();
   const isMobile = useMediaQuery("(max-width: 768px)");
   const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
   const { selectedBaseKnowledge } = useAiSourceSelector();
   const { user } = useAuth();
   const textareaRef = useRef<HTMLTextAreaElement>(null);
   const translation = useTranslations("Home");
   const isSharedView = Boolean(activeShareKey);
   const isCompactLayout = isMobile || isTablet;
   const effectiveChatHistorySidebarWidth = useMemo(() => {
      if (isCompactLayout) return chatHistorySidebarWidth;

      return getViewportClampedSidebarWidth(chatHistorySidebarWidth, viewportWidth);
   }, [chatHistorySidebarWidth, isCompactLayout, viewportWidth]);
   const isNewChat = useMemo(() => {
      return !historyItem?.id && !lastSendMessageEvent?.text && !activeShareKey;
   }, [historyItem?.id, lastSendMessageEvent, activeShareKey]);
   const isMainEmptyState = isNewChat || isChatUnavailable;

   const effectiveKnowledgeBaseId = isSharedView
      ? sharedKnowledgeBaseId || selectedBaseKnowledge.id
      : selectedBaseKnowledge.id;

   const hasAccessToSharedProject = useMemo(() => {
      if (!isSharedView || !effectiveKnowledgeBaseId) return true;
      return user?.projects?.includes(effectiveKnowledgeBaseId as PROJECTS) ?? false;
   }, [isSharedView, effectiveKnowledgeBaseId, user?.projects]);

   const sharedProjectUpgradeUrl = useMemo(() => {
      if (hasAccessToSharedProject) return "";
      const protocol =
         typeof window !== "undefined" ? window.location.protocol + "//" : "https://";
      const upgradeUrlMap: Partial<Record<PROJECTS, string>> = {
         [PROJECTS.CRYPTOCURRENCY_MARKET]:
            protocol + DOMAIN.CRYPTOCURRENCY_MARKET + "/shop",
         [PROJECTS.KINGS_AND_ALLIES]: `${protocol}${DOMAIN.KINGS_AND_ALLIES}/${locale}/shop`
      };
      return upgradeUrlMap[effectiveKnowledgeBaseId as PROJECTS] || "";
   }, [hasAccessToSharedProject, effectiveKnowledgeBaseId, locale]);

   const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
         if ((e.metaKey || e.ctrlKey) && e.key === "k") {
            e.preventDefault();
            setSearchDialogOpen(true);
         }
      },
      [setSearchDialogOpen]
   );

   const handleForkSharedChat = useCallback(async () => {
      if (isForking || !activeShareKey) return;

      setIsForking(true);
      try {
         const response = await fetchWithAuth(api_url.chat_history_fork_url, {
            method: METHOD_TYPE.POST,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               shareKey: activeShareKey,
               knowledgeBase: [selectedBaseKnowledge.id]
            })
         });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || translation("forkChatFailed"));
         }

         const result = await response.json();
         const forkedChatId = result.data?.id;

         if (!forkedChatId) {
            throw new Error(translation("forkChatFailed"));
         }

         logger.info("[ForkChat] Successfully forked shared chat", {
            shareKey: activeShareKey,
            forkedChatId
         });

         triggerHistoryRefetch();
         router.replace(`/${locale}/c/${forkedChatId}`);
      } catch (err: any) {
         logger.error("[ForkChat] Fork failed:", { error: err.message });
         toast({
            title: translation("forkChatFailed"),
            description: err.message,
            variant: "destructive"
         });
      } finally {
         setIsForking(false);
      }
   }, [
      isForking,
      activeShareKey,
      selectedBaseKnowledge.id,
      translation,
      triggerHistoryRefetch,
      router,
      locale,
      toast
   ]);

   const handleStartNewChat = useCallback(() => {
      resetState();
      setActiveShareKey("");
      setIsChatUnavailable(false);
      router.push("/");
   }, [resetState, router]);

   useEffect(() => {
      initModel();
   }, []);

   useEffect(() => {
      const syncViewportWidth = () => {
         setViewportWidth(window.innerWidth);
      };

      syncViewportWidth();
      window.addEventListener("resize", syncViewportWidth);

      return () => {
         window.removeEventListener("resize", syncViewportWidth);
      };
   }, []);

   useEffect(() => {
      window.addEventListener("keydown", handleKeyDown);
      return () => {
         window.removeEventListener("keydown", handleKeyDown);
      };
   }, [handleKeyDown]);

   useEffect(() => {
      if (activeShareKey) {
         setHistoryItem(null);
      }
   }, [activeShareKey, setHistoryItem]);

   useEffect(() => {
      setIsChatUnavailable(false);
   }, [shareKeyProp]);

   const typePlaceholder = useMemo(() => {
      switch (selectedBaseKnowledge.id) {
         case PROJECTS.CRYPTOCURRENCY_MARKET:
            return translation("typePlaceholderCryptoCurrencyMarket");
         case PROJECTS.KINGS_AND_ALLIES:
            return translation("typePlaceholderKingsAndAllies");
         default:
            return "Type your message...";
      }
   }, [translation, selectedBaseKnowledge]);

   return (
      <>
         {isShowChatHistory && (
            <ChatHistory
               shareKey={activeShareKey}
               isOpen={isOpenHistoryChat}
               onOpenChange={setOpenHistoryChat}
               sidebarWidth={effectiveChatHistorySidebarWidth}
               onSidebarWidthChange={setChatHistorySidebarWidth}
               isResizing={isChatHistorySidebarResizing}
               onResizeStateChange={setIsChatHistorySidebarResizing}
               className={`z-30 ${isCompactLayout ? "fixed" : "absolute"}`}
            />
         )}
         <div
            className={`flex flex-col h-screen ${
               isChatHistorySidebarResizing ? "" : "transition-all duration-300"
            }`}
            style={{
               marginLeft: isShowChatHistory
                  ? isOpenHistoryChat && !isCompactLayout
                     ? effectiveChatHistorySidebarWidth
                     : 64
                  : undefined
            }}
         >
            <ChatMessages
               inputRefFocusAfterFinishChat={textareaRef}
               shareKey={activeShareKey}
               onShareComplete={handleShareComplete}
               onChatUnavailable={setIsChatUnavailable}
               className={cn("px-3", {
                  hidden: isMainEmptyState
               })}
            />
            <div
               className={cn({
                  "flex-1 flex flex-col items-center justify-center px-4 py-8 max-h-[calc(100vh-200px)]":
                     isMainEmptyState
               })}
            >
               <div
                  className={cn({
                     "w-full max-w-3xl space-y-6": isMainEmptyState,
                     "w-full": !isMainEmptyState
                  })}
               >
                  {isChatUnavailable ? (
                     <div className="mx-auto flex max-w-lg flex-col items-center text-center">
                        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground">
                           <ShieldOff size={26} strokeWidth={1.8} />
                        </div>
                        <h1 className="text-2xl font-semibold text-foreground">
                           {translation("chatUnavailableTitle")}
                        </h1>
                        <p className="mt-3 max-w-md text-sm leading-6 text-muted-foreground">
                           {translation("chatUnavailableDescription")}
                        </p>
                        <Button
                           onClick={handleStartNewChat}
                           className="mt-7 rounded-full gap-2 px-5"
                        >
                           <MessageSquarePlus size={18} strokeWidth={2.2} />
                           {translation("newChat")}
                        </Button>
                     </div>
                  ) : (
                     <div
                        className={cn({
                           "text-center space-y-1 mb-8": isNewChat,
                           hidden: !isNewChat
                        })}
                     >
                        <TaiZenLogo size="sm" />

                        <h1
                           className={cn(
                              "text-2xl w-full text-gray-900 dark:text-gray-100",
                              {
                                 "sm:text-xl": locale === "vi",
                                 "sm:text-2xl": locale !== "vi",
                                 "text-lg": isMobile || isTablet
                              }
                           )}
                        >
                           {typePlaceholder}
                        </h1>
                        {isTemporaryChat && (
                           <div className="flex flex-col items-center text-slate-500 dark:text-slate-400">
                              <span className="text-xs">
                                 <b>{translation("temporaryMode")}</b>
                                 {" - "}
                                 {translation("temporaryModeDescription")}
                              </span>
                           </div>
                        )}
                     </div>
                  )}

                  {isChatUnavailable ? null : isSharedView ? (
                     <div className="flex justify-center py-6">
                        {hasAccessToSharedProject ? (
                           <Button
                              variant="outline"
                              onClick={handleForkSharedChat}
                              disabled={isForking}
                              className="rounded-full bg-transparent text-gray-600 hover:text-primary dark:text-gray-200 dark:hover:text-primary gap-2 h-[36px] px-5"
                           >
                              {isForking ? (
                                 <>
                                    <Loading size="sm" />
                                    {translation("forkingChat")}
                                 </>
                              ) : (
                                 <>
                                    <ArrowRight size={18} strokeWidth={2.5} />
                                    {translation("continueConversation")}
                                 </>
                              )}
                           </Button>
                        ) : (
                           <Button
                              variant="outline"
                              onClick={() => {
                                 if (sharedProjectUpgradeUrl) {
                                    window.open(
                                       sharedProjectUpgradeUrl,
                                       "_blank",
                                       "noopener,noreferrer"
                                    );
                                 }
                              }}
                              className="rounded-full bg-amber-500/10 border-amber-500/30 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/20 gap-2 h-[36px] px-5"
                           >
                              <Lock size={16} strokeWidth={2.5} />
                              {translation("upgradeToAccess")}
                           </Button>
                        )}
                     </div>
                  ) : (
                     isShowChatInput && (
                        <ChatInput
                           loading={loading}
                           error={error}
                           textareaRef={textareaRef}
                           isEmpty={isNewChat}
                           className={cn({
                              "w-full": isNewChat,
                              "px-4 py-4 pb-8": !isNewChat
                           })}
                        />
                     )
                  )}
               </div>
            </div>
         </div>

         <ChatSearchDialog shareKey={activeShareKey} />
      </>
   );
}
